import { useState, useEffect } from 'react';
import { Container, Group, Button, NumberInput, Select } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  IDispenser,
  IDispenserRequest,
  addDispenserDetails,
  updateDispenserDetails,
  deleteDispenser
} from '../../../libraries/dispensers';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import StatusOperModal from '../../components/status-oper-modal';
import { Language } from '../../languages/languageHandler';
import { IProductResponse, fetchProductsList } from '../../../libraries/products';

interface AddEditDispenserProps {
  dispenser?: IDispenser;
  mode: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
  eid?: number;
}

const AddEditDispenser = ({ dispenser, mode, opened, close, onSaved, eid }: AddEditDispenserProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = new AbortController();

  const [products, setProducts] = useState<IProductResponse>();
  const [formData, setFormData] = useState<IDispenserRequest>({
    dispenserid: dispenser?.dispenserid || 0,
    eid: dispenser?.eid || eid || 0,
    productid: dispenser?.productid || '',
    newQuantity: dispenser?.quantity || 0,
    quantity: dispenser?.quantity || 0
  });

  const [loading, setLoading] = useState(false);
  const [statusOper, setStatusOper] = useState<any>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'edit' | 'delete'>('add');

  // Load product list once
  useEffect(() => {
    const loadProductList = async () => {
      setLoading(true);
      try {
        const response = await fetchProductsList();
        if (response.statusOper.code === 0) setProducts(response);
      } catch (error: any) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadProductList();
  }, []);

  // Initialize formData when dispenser, mode, or products change
  useEffect(() => {
    if (!products?.list?.length) return;

    if (mode === 'new') {
      setFormData(prev => ({
        ...prev,
        eid: eid || 0,
        productid: products.list[0].productid
      }));
    } else if (mode === 'edit' && dispenser) {
      const matched = products.list.find(p => p.productid === dispenser.productid);
      setFormData(prev => ({
        ...prev,
        dispenserid: dispenser.dispenserid,
        eid: dispenser.eid,
        productid: matched ? matched.productid : products.list[0].productid,
        newQuantity: dispenser.quantity,
        quantity: dispenser.quantity
      }));
    }
  }, [products, dispenser, mode, eid]);

  const handleChange = (field: keyof IDispenserRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showStatus = (status: any, action: 'add' | 'edit' | 'delete') => {
    setStatusOper(status);
    setActionType(action);
    setShowStatusModal(true);

    if (status.code === 0) {
      onSaved?.();
      setTimeout(() => {
        setShowStatusModal(false);
        close();
      }, 200000);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setActionType(mode === 'new' ? 'add' : 'edit');
    try {
      const response =
        mode === 'new'
          ? await addDispenserDetails(formData, controller)
          : await updateDispenserDetails(formData, controller);

      showStatus(response.statusOper, mode === 'new' ? 'add' : 'edit');
    } catch (err: any) {
      const statusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'Unexpected error'
      };
      showStatus(statusOper, mode === 'new' ? 'add' : 'edit');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!dispenser) return;
    setLoading(true);
    setActionType('delete');
    try {
      const response = await deleteDispenser(dispenser.entid, dispenser.eid, dispenser.dispenserid);
      showStatus(response.statusOper, 'delete');
    } catch (err: any) {
      const statusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'Unexpected error'
      };
      showStatus(statusOper, 'delete');
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.DISPENSERS_ADD : `${lang.DISPENSERS_EDIT} - ${dispenser?.dispenserid}`;

  return (
    <>
      <ModalComponent
        show={opened}
        handleClick={close}
        title={title}
        content={
          <Container className="dispenser-details-modal">
            <Group className="dispenser-details">
              <NumberInput
                label={lang.EQUIPMENT_ID}
                value={formData.eid}
                onChange={value => handleChange('eid', value || 0)}
                disabled={mode !== 'new' || loading || !!eid}
              />
              <NumberInput
                label={lang.DISPENSER_ID}
                value={formData.dispenserid}
                onChange={value => handleChange('dispenserid', value || 0)}
                disabled={mode !== 'new' || loading}
                min={0}
              />
              <Select
                label={lang.DISPENSER_PRODUCT}
                value={formData.productid?.toString()}
                onChange={value => handleChange('productid', value)}
                data={
                  products?.list?.map(p => ({
                    value: p.productid.toString(),
                    label: `${p.productid} - ${p.name}`
                  })) || []
                }
                disabled={loading}
                placeholder="Select product"
              />
            </Group>

            <Group className="dispensers-buttons">
              <Button radius="md" onClick={handleSave} disabled={loading}>
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>
              {mode === 'edit' && (
                <Button radius="md" onClick={handleDelete} disabled={loading}>
                  {lang.GLOBAL_DELETE}
                </Button>
              )}
            </Group>
          </Container>
        }
      />

      {/* Status Modal */}
      {statusOper && (
        <StatusOperModal
          opened={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          statusOper={statusOper}
          successMessage={
            statusOper.code === 0
              ? actionType === 'add'
                ? lang.DISPENSERS_ADD_SUCCESS
                : actionType === 'edit'
                  ? lang.DISPENSERS_EDIT_SUCCESS
                  : lang.DISPENSERS_DELETE_SUCCESS
              : undefined
          }
          errorMessage={lang.DISPENSERS_ADD_ERROR} // always generic error message
        />
      )}
    </>
  );
};

export default AddEditDispenser;
